import { SAFARI_DOM_TOUCH_TYPES } from '../../../../client/utils/domConstants';
import { XYCoord } from 'dnd-core';
import { distance } from './math';
import { getEventClientOffset } from './offsets';

// Used for MouseEvent.buttons (note the s on the end).
const MouseButtons = {
    Left: 1,
    Right: 2,
    Center: 4,
};

// Used for e.button (note the lack of an s on the end).
const MouseButton = {
    Left: 0,
    Center: 1,
    Right: 2,
};

/**
 * Only touch events and mouse events where the left button is pressed should initiate a drag.
 * @param {MouseEvent | TouchEvent} e The event
 */
export function eventShouldStartDrag(e: any) {
    // For touch events, button will be undefined. If e.button is defined,
    // then it should be MouseButton.Left.
    return (e instanceof TouchEvent && e.touches.length === 1) || e.button === MouseButton.Left;
}

/**
 * When dragging table element's column resize handle, the touchmove event from this file will
 * override the pointermove event from the resize behaviour, which will scroll the Canvas instead.
 * We want to prevent this scroll to end the resize.
 */
export function eventShouldDragFromTarget(e: TouchEvent | MouseEvent) {
    if (e.target && (e.target as HTMLElement).dataset.ignoreTouchMove) {
        // return true to indicate that we've handled the event
        return false;
    }

    return true;
}

/**
 * Only touch events and mouse events where the left mouse button is no longer held should end a drag.
 * It's possible the user mouse downs with the left mouse button, then mouse down and ups with the right mouse button.
 * We don't want releasing the right mouse button to end the drag.
 * @param {MouseEvent | TouchEvent} e The event
 */
export function eventShouldEndDrag(e: any) {
    // Touch events will have buttons be undefined, while mouse events will have e.buttons's left button
    // bit field unset if the left mouse button has been released
    return e.buttons === undefined || (e.buttons & MouseButtons.Left) === 0;
}

export function isTouchEvent(e: Touch | TouchEvent | MouseEvent): e is TouchEvent {
    return !!(e as TouchEvent).targetTouches;
}

/**
 * Determines whether the event is a touch event using the Apple Pencil
 */
export function isApplePencilTouchMove(e: Touch | TouchEvent | MouseEvent): e is TouchEvent {
    // @ts-ignore - TS doesn't know about touchType yet
    return isTouchEvent(e) && e.touches?.[0]?.touchType === SAFARI_DOM_TOUCH_TYPES.STYLUS;
}

/**
 * Determines if the stylus's movement is small enough to be ignored when starting a drag.
 */
export function isSmallStylusMovement(e: TouchEvent, initialCoord: Partial<XYCoord>) {
    const clientOffset = getEventClientOffset(e);

    if (!clientOffset) return false;

    const moveDist = distance(initialCoord.x || 0, initialCoord.y || 0, clientOffset.x, clientOffset.y);

    return moveDist < 10;
}
